'use client'

import React, { useEffect, useState } from 'react'
import { motion, Variants } from 'framer-motion'
import classes from './index.module.css'
import { useTranslations } from 'next-intl'
import { LiquidMetalSurface } from '@/components/ui/LiquidMetalSurface'
import { ShippingIcon, GuaranteeIcon, SupportIcon, PaymentIcon } from './TrustIcons'

/**
 * AnimatedFeatures — trust band of four clear liquid-metal cards.
 *
 * Every card shows the static clear-metal face; a single spotlight moves across
 * them, running the live WebGL liquid-metal shader on ONE card at a time. It
 * auto-advances every ROTATE_MS, and hovering a card takes over the spotlight
 * (auto-advance resumes on mouse leave). One live context at a time keeps the
 * GPU cost flat no matter how many cards there are.
 */

const ROTATE_MS = 5000

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
}

const itemVariants: Variants = {
  hidden: { y: 16, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.45, ease: 'easeOut' } },
}

const AnimatedFeatures: React.FC = () => {
  const t = useTranslations('features')

  const features = [
    { key: 'shipping', title: t('shipping.title'), description: t('shipping.description'), Icon: ShippingIcon },
    { key: 'quality', title: t('quality.title'), description: t('quality.description'), Icon: GuaranteeIcon },
    { key: 'support', title: t('support.title'), description: t('support.description'), Icon: SupportIcon },
    { key: 'payment', title: t('payment.title'), description: t('payment.description'), Icon: PaymentIcon },
  ]

  const [activeIndex, setActiveIndex] = useState(0)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)

  // Auto-advance the spotlight, but pause while a card is hovered.
  useEffect(() => {
    if (hoverIndex !== null) return
    // 7b: respect the OS setting — no auto-rotating spotlight under reduced motion.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % features.length)
    }, ROTATE_MS)
    return () => clearInterval(id)
  }, [hoverIndex, features.length])

  // Hover takes over the spotlight.
  const spotlight = hoverIndex ?? activeIndex

  return (
    <motion.section
      data-testid="animated-features"
      className={classes.grid}
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-80px' }}
    >
      {features.map((feature, index) => (
        <motion.div
          key={feature.key}
          variants={itemVariants}
          data-testid="feature-item"
          onMouseEnter={() => setHoverIndex(index)}
          onMouseLeave={() => setHoverIndex((h) => (h === index ? null : h))}
          onFocus={() => setHoverIndex(index)}
          onBlur={() => setHoverIndex((h) => (h === index ? null : h))}
        >
          <LiquidMetalSurface
            interactive
            active={index === spotlight}
            radius={1.75}
            className={classes.card}
          >
            <div className={classes.layout}>
              <span className={classes.iconWrap} aria-hidden="true">
                <feature.Icon className={classes.icon} size={22} />
              </span>
              <div className={classes.text}>
                <h3 className={classes.title}>{feature.title}</h3>
                <p className={classes.description}>{feature.description}</p>
              </div>
            </div>
          </LiquidMetalSurface>
        </motion.div>
      ))}
    </motion.section>
  )
}

export default AnimatedFeatures
